<template>
  <div ref="frame" class="frame" :style="[frameStyle, currentProjectThemeStyle]">
    <header ref="header" class="tb-header">
      <div class="tb-header-bg"></div>
      <div ref="userBox" class="user-box">
        <div class="user-name">Hi! 欢迎回来</div>
        <div class="user-sub">{{ todayText }}</div>
      </div>
    </header>

    <TaskNav :active="active" @change="onChange" />

    <!-- 首页/表格（test.html 风格两栏总览） -->
    <div v-if="active === 'home'" class="tb-container">
      <div class="sidebar">
        <div class="repo-panel">
          <div class="header">
            <div class="title">
              <i class="fas fa-folder"></i>
              我的工作区
            </div>
            <!--
              「分享我的内容给好友」入口：原挂在「朋友」页标题栏，
              按需求迁到时间表页，贴在「我的工作区」这一行的最右边。
              放在 .header（space-between）里而不是 .title 里 → 由父级撑到右边缘，
              不会紧跟标题文字、也不会跟着标题长度左右跑。
            -->
            <button
              class="share-btn"
              type="button"
              title="分享我的内容给好友"
              aria-label="分享我的内容给好友"
              @click="openShareDialog"
            >
              <FaIcon :icon="faShareFromSquare" :size="20" />
            </button>
          </div>
          <!-- 搜索框：图标用 flex 固定不收缩，输入框 min-width:0 才能跟着容器变窄 -->
          <div class="search">
            <FaIcon :icon="faMagnifyingGlass" :size="14" class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="搜索工作表..."
              aria-label="搜索工作表"
              @keydown.esc.prevent="clearSearch"
            >
            <button
              v-if="isSearching"
              type="button"
              class="search-clear"
              title="清空搜索"
              aria-label="清空搜索"
              @click="clearSearch"
            >
              <FaIcon :icon="faXmark" :size="12" />
            </button>
          </div>
          <!-- 搜索结果计数：只在搜索时出现，让"为什么只剩这几条"有解释 -->
          <div v-if="isSearching" class="search-meta">
            找到 {{ filteredWorkspaces.length }} / {{ workspaces.length }} 个工作表
          </div>
          <!-- 新建工作表：上移到列表上方，底部空间留给「自己的实时状态」 -->
          <div class="create" @click="openCreateWorkspace">
            ＋ 新建工作表
          </div>
          <div class="repo-list">
            <div v-if="!filteredWorkspaces.length" class="repo-empty">
              <template v-if="isSearching">
                没有匹配「{{ searchQuery.trim() }}」的工作表
              </template>
              <template v-else>
                暂无工作表，点击下方按钮新建
              </template>
            </div>
            <div
              v-for="ws in filteredWorkspaces"
              :key="ws.id"
              :class="['repo-item', { active: String(ws.id) === String(currentProjectId), pinned: ws.pinned }]"
              @click="selectWorkspace(ws)"
            >
              <div class="repo-icon" :style="{ color: ws.color }">
                <i v-if="ws.iconClass" :class="ws.iconClass"></i>
                <template v-else>{{ ws.letter }}</template>
              </div>
              <div class="repo-name" :style="{ color: ws.pinned ? ws.color : '' }">
                <span
                  v-for="(part, i) in nameParts(ws.name)"
                  :key="i"
                  :class="{ 'name-hit': part.hit }"
                >{{ part.text }}</span>
              </div>
              <div class="repo-actions">
                <button
                  class="repo-action-btn"
                  title="修改"
                  @click.stop="editWorkspace(ws)"
                >
                  <i class="fas fa-pen-to-square"></i>
                </button>
                <button
                  class="repo-action-btn danger"
                  title="删除"
                  @click.stop="deleteWorkspace(ws)"
                >
                  <i class="fas fa-trash"></i>
                </button>
                <button
                  :class="['repo-action-btn', 'pin', { active: ws.pinned }]"
                  :title="ws.pinned ? '取消收藏' : '收藏'"
                  @click.stop="togglePin(ws)"
                >
                  <i class="fas fa-thumbtack"></i>
                </button>
              </div>
            </div>
          </div>
          <!--
            底部：自己的实时状态（在做 / 上次）。

            样式与「朋友」页的好友卡片完全一致（头像 + 昵称 + 动态行两行文字），
            这样同一套信息在两页看起来是同一个东西 —— 区别只是这里恒为自己：
            头像/昵称取 profileState（与顶栏 TaskNav 同一份数据，改头像即时同步），
            「在做」时头像外圈带主色呼吸光环（UserAvatar 的 onair）。
          -->
          <div v-if="selfActivity" class="self-activity">
            <div class="self-card">
              <UserAvatar
                class="self-avatar"
                :url="meAvatar"
                :name="meName"
                :onair="selfRunning"
              />
              <div class="self-info">
                <div class="self-name">{{ meName }}</div>
                <FriendActivityLine :activity="selfActivity" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel main">
        <transition name="list-page-switch" mode="out-in">
          <ListPage
            v-if="currentProjectId"
            :key="currentProjectId"
            :workspace-id="currentWorkspaceId"
            :project-id="currentProjectId"
            :project-name="currentProjectName"
            :project-description="currentProjectDescription"
            :project-color="currentProjectColor"
            :project-icon-class="currentProjectIconClass"
            :project-letter="currentProjectLetter"
            :theme-style="currentProjectThemeStyle"
            @activity-changed="onActivityChanged"
          />
        </transition>
        <div v-if="!currentProjectId" class="empty-tip">
          暂无选中的工作表，请在左侧选择一个工作表
        </div>
      </div>

      <!--
        「分享我的内容给好友」弹窗：随入口一起从「朋友」页迁到时间表页。

        ⚠️ 必须挂在 .tb-container 里面、**不能**挂到 .frame 根下：
        本文件有 `.frame > *{ position:relative }`，而 el-dialog 的根 .el-dialog__wrapper
        本身是 position:fixed —— 一旦它成为 .frame 的直接子元素，就会被这条规则（还带
        scoped 属性选择器，优先级更高）压成 relative，掉进文档流，
        表现就是「页面底部凭空多了一个板块」而不是悬浮窗；遮罩 .v-modal 同理。
        放进 .tb-container（弹窗是 fixed，不参与 grid 排布）就不会命中那条规则。
      -->
      <ShareSettingDialog
        :visible="shareVisible"
        @close="closeShareDialog"
      />
    </div>

    <!-- 随想子页 -->
    <div v-else-if="active === 'thoughts'" class="tb-content">
      <ThoughtsPage />
    </div>

    <!-- 朋友子页 -->
    <div v-else-if="active === 'friends'" class="tb-content">
      <FriendsPage />
    </div>

  <!-- 个人中心（动态能力已迁入这里的「用户动态」分区） -->
  <div v-else-if="active === 'profile'" class="tb-content">
    <ProfilePage />
  </div>

  <CreateWorkspaceDialog
    :visible="showCreateWorkspace"
    :mode="dialogMode"
    :project="editingProject"
    :workspaceId="currentWorkspaceId"
    @close="showCreateWorkspace = false"
    @created="onWorkspaceCreated"
    @updated="onWorkspaceUpdated"
    @visible-change="onDialogVisibleChange"
  />

  <!-- 成就解锁提醒：挂在本层，任何子页解锁成就都能弹出 -->
  <AchievementUnlockDialog />

  <!--
    「在做」的定时提醒：同样挂在本层（而不是 ListPage 里）——
    定时到点时用户未必停在表格页（可能在随想 / 朋友 / 个人中心），
    弹在 .frame 根下 + modal-append-to-body=false，任何子页都能选「继续 / 结束」，
    并且能继承 .frame 上的 --lp-* 工作表配色（挂载细节见 RunningTimerDialog 顶部注释）。
  -->
  <RunningTimerDialog
    :entry="currentTimerEntry"
    @dismiss="onTimerDismiss"
    @continue="onTimerContinue"
    @end="onTimerEnd"
  />
</div>
</template>

<script>
import router from '@/router'
import TaskNav from './TaskNav.vue'
import ListPage from '@/components/list/ListPage.vue'
import ThoughtsPage from './ThoughtsPage.vue'
import FriendsPage from './FriendsPage.vue'
import ProfilePage from './ProfilePage.vue'
import CreateWorkspaceDialog, { PRESET_ICONS, PROJECT_PALETTE } from './CreateWorkspaceDialog.vue'
// 「在做」的定时提醒弹窗
import RunningTimerDialog from '@/components/list/RunningTimerDialog.vue'
// 「分享我的内容给好友」弹窗：从「朋友」页迁过来的分享设置
import ShareSettingDialog from './friend/ShareSettingDialog.vue'
import FriendActivityLine from './friend/FriendActivityLine.vue'
import AchievementUnlockDialog from '@/components/achievement/AchievementUnlockDialog.vue'
import { runAchievementCheck } from '@/components/achievement/achievementStore.js'
import FaIcon from '@/components/common/FaIcon.vue'
// 自己的实时状态卡片要画头像：与好友卡片共用同一个头像组件（首字母兜底 / onair 光环都在里面）
import UserAvatar from '@/components/common/UserAvatar.vue'
// 搜索框：放大镜 + 清空用的叉号（走子路径引入，只打包这两个图标）
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlass'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
// 分享入口图标：从方框里射出的箭头（"把内容分享出去"）
import { faShareFromSquare } from '@fortawesome/free-solid-svg-icons/faShareFromSquare'
import { resolveImageUrl } from '@/utils/avatar.js'
import { profileState, shadeColor, hexToRgba } from './profileStorage'
import { listWorkspaces } from '@/api/workspace.js'
import { listProjects, deleteProject } from '@/api/project.js'
import { createTag } from '@/api/tag.js'
import { resolvePending, timerState, restoreTimers } from '@/utils/runningTimer.js'
// 「在做」结束（含跨天拆块）的共享实现：表格页的「结束」按钮和这里的提醒弹窗走同一条路
import { endRunningBlock } from '@/utils/runningEnd.js'
import { notifySuccess } from '@/utils/notify.js'
import { DEFAULT_CATEGORY_PRESETS, buildTagName } from '@/components/list/constants.js'

function fmtDate(d) {
  const w = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()]
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${w}`
}

export default {
  name: 'TaskBlockPage',
  components: {
    TaskNav,
    ListPage,
    ThoughtsPage,
    FriendsPage,
    ProfilePage,
    CreateWorkspaceDialog,
    ShareSettingDialog,
    FriendActivityLine,
    AchievementUnlockDialog,
    RunningTimerDialog,
    FaIcon,
    UserAvatar
  },
  // 由 App.vue 传入当前路由的 sub（二级页面）
  // - null  -> 显示首页/表格（test.html 风格两栏总览）
  // - 'thoughts' | 'friends' | 'profile' -> 显示对应子页
  props: {
    sub: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      faMagnifyingGlass,
      faXmark,
      faShareFromSquare,
      // 搜索框关键词：空串 = 不筛选，展示全部工作表
      searchQuery: '',
      // 自己的实时状态（在做 / 上次）：由 ListPage 的区间响应上抛，展示在左侧底部
      selfActivity: null,
      showCreateWorkspace: false,
      // 「分享我的内容给好友」弹窗开关（入口在左侧「我的工作区」标题右侧）
      shareVisible: false,
      // 「新建工作表」弹窗打开期间：本页折叠手势停摆、.main 原生滚动关掉
      dialogScrollLock: false,
      // .main 被改成 overflow:hidden 前的原值（null = 当前没被锁）
      savedMainOverflow: null,
      dialogMode: 'create',
      editingProject: null,
      currentWorkspaceId: null,
      currentProjectId: null,
      workspaces: [],
      pinnedIds: [],
      // 手势目标值（collapseOffset）与当前渲染值（renderOffset）分离，
      // 由 rAF 每帧插值逼近，避免滚轮一格 100px 造成的跳变
      collapseOffset: 0,
      renderOffset: 0,
      smoothing: 0.28,
      rafId: 0,
      lastTouchY: 0,
      isNarrow: window.innerWidth <= 1100
    }
  },
  created() {
    this.loadPinnedIds()
    this.loadWorkspaceAndProjects().then(() => {
      if (this.pinnedIds.length === 0 && this.workspaces.length) {
        this.pinProject(this.workspaces[0].id)
      }
    })
    // 进入 TaskBlock 时做一次成就检测：补齐「在别处操作后未触发检测」的解锁
    runAchievementCheck(true)
    // 恢复「在做」的定时提醒：放在这一层（而不是 ListPage）是因为用户进来时可能
    // 停在随想 / 朋友页，定时也要照样到点。ListPage 里还会再调一次，幂等。
    restoreTimers()
  },
  computed: {
    // 当前激活的内部 key
    active() {
      return this.sub || 'home'
    },
    /**
     * 自己的昵称 / 头像：与顶栏 TaskNav、个人中心共用 profileState（Vue.observable）。
     * 头像上传或改名后，这里无需额外监听就会跟着变。
     */
    meName() {
      return (profileState.nickname || '').trim() || '我'
    },
    meAvatar() {
      return profileState.avatar || ''
    },
    /** 自己是否「正在做」：决定头像外圈是否亮主色呼吸光环，与好友卡片同一套语义 */
    selfRunning() {
      return !!(this.selfActivity && this.selfActivity.status === 'RUNNING')
    },
    todayText() {
      return fmtDate(new Date())
    },
    /**
     * 「在做」定时提醒弹窗当前要显示的条目（timerState.queue[0]）。
     * queue 是 Vue.observable 的数组 → 到点时这里自动变成有条目，弹窗随之弹出。
     */
    currentTimerEntry() {
      return timerState.queue[0] || null
    },
    /**
     * 归一化后的搜索关键词：去首尾空格 + 转小写。
     * 为空串表示「不搜索」，此时列表原样展示。
     */
    searchKeyword() {
      return (this.searchQuery || '').trim().toLowerCase()
    },
    isSearching() {
      return this.searchKeyword.length > 0
    },
    /**
     * 按关键词过滤后的工作表列表。
     * 只匹配名称（工作表也叫「工作表」，搜的是它自己），保留后端返回的原始顺序，
     * 不因为搜索改变排序 —— 避免选中项在输入过程中来回跳动。
     */
    filteredWorkspaces() {
      const q = this.searchKeyword
      if (!q) return this.workspaces
      return this.workspaces.filter(ws => (ws.name || '').toLowerCase().includes(q))
    },
    currentProjectName() {
      const ws = this.workspaces.find(w => String(w.id) === String(this.currentProjectId))
      return ws ? ws.name : ''
    },
    currentProjectDescription() {
      const ws = this.workspaces.find(w => String(w.id) === String(this.currentProjectId))
      return ws ? ws.description : ''
    },
    /**
     * 当前选中工作表的主色（hex，存库字段）。
     * 没选工作表时返回空串。
     */
    currentProjectColor() {
      const ws = this.workspaces.find(w => String(w.id) === String(this.currentProjectId))
      return ws ? (ws.color || '') : ''
    },
    /**
     * 当前工作表的 Font Awesome 图标类（预设图标才有，如 'fas fa-clock'）。
     * 透传给 ListPage，渲染在「我的xx」标题左侧，与左侧工作区列表里的图标保持一致。
     */
    currentProjectIconClass() {
      const ws = this.workspaces.find(w => String(w.id) === String(this.currentProjectId))
      return ws ? (ws.iconClass || '') : ''
    },
    /**
     * 当前工作表的首字母兜底（工作表没选预设图标时显示它，而不是空一块）。
     */
    currentProjectLetter() {
      const ws = this.workspaces.find(w => String(w.id) === String(this.currentProjectId))
      return ws ? (ws.letter || '') : ''
    },
    /**
     * 把当前工作表的 hex 派生为一整套 `--lp-*` 变量集合，挂在 .frame 根元素上，
     * 让**整个板块**跟随工作表配色：左侧工作区面板（.repo-panel 及其按钮）
     * + 右侧时间表（TimeTable 表头/行 / TimeStats / CategoryLegend）。
     *
     * ⚠️ **仅在「表格」子页（active === 'home'）生效**。随想/朋友/动态/个人中心
     *    跟"工作表"没关系，保持原本配色。
     *
     * - 没选工作表时返回空对象，走 App.vue 的全局主题色；
     * - 工作表色不在 10 种新色板里时（旧数据未迁移）也不覆盖，行为同未选；
     * - 覆盖的层级：主色 / 深主色 / 两级浅背景 / 两级边框 / 页面底色 / 次表面；
     * - **刻意不动** --lp-surface（保持纯白）与 --lp-text*（保持中性灰），
     *   否则整块卡片都染色后正文会失去对比度。
     * - 顶栏用的是 --tb-*（用户个人主题色），**不受这里影响** —— 个人主题色与
     *   工作表色是两个维度，顶栏保持个人设置的颜色。
     */
    currentProjectThemeStyle() {
      // 只在「表格」子页生效：随想 / 朋友 / 动态 / 个人中心保持原本的配色，
      // 不被工作表色系污染 —— 它们跟"工作表"没有关系。
      if (this.active !== 'home') return {}

      const color = this.currentProjectColor
      if (!color) return {}
      const entry = PROJECT_PALETTE.find(c => c.active.toLowerCase() === color.toLowerCase())

      // 色不在色板里（旧数据未迁移）→ 不覆盖，走默认；
      // 选了「默认」色 → 同样不覆盖，走回 App.vue 的浅绿默认主题。
      if (!entry || entry.isDefault) return {}

      return {
        '--lp-primary': entry.active,
        '--lp-primary-dark': entry.dark,
        '--lp-primary-soft': entry.soft,
        '--lp-primary-soft2': entry.soft2,
        '--lp-border': entry.border,
        '--lp-border-strong': entry.borderStrong,
        '--lp-bg': entry.bg,
        '--lp-surface-2': entry.surface2
      }
    },
    frameStyle() {
      const theme = profileState.theme || '#5b9147'
      const wallpaper = profileState.wallpaper || 'https://image.suisuisu.cn/upload/2607/d1b502465354b537.png'
      return {
        '--tb-primary': theme,
        '--tb-primary-dark': shadeColor(theme, -20),
        '--tb-nav-bg': theme,
        '--tb-nav-item-bg': shadeColor(theme, 20),
        '--tb-nav-item-hover': shadeColor(theme, 35),
        '--tb-nav-active-bg': shadeColor(theme, -25),
        '--tb-header-overlay': hexToRgba(theme, 0.35),
        // 用户自己上传的壁纸同样存在又拍云（只有 http），https 页面下会被拦掉，走同源代理
        '--tb-wallpaper': `url('${resolveImageUrl(wallpaper)}')`
      }
    },
    fullHeaderHeight() {
      return this.isNarrow ? 210 : 270
    }
  },
  mounted() {
    this.onResize()
    window.addEventListener('resize', this.onResize)
    const el = this.$refs.frame
    if (el) {
      el.addEventListener('wheel', this.onWheel, { passive: false })
      el.addEventListener('touchstart', this.onTouchStart, { passive: true })
      el.addEventListener('touchmove', this.onTouchMove, { passive: false })
    }
    this.applyHeader()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    const el = this.$refs.frame
    if (el) {
      el.removeEventListener('wheel', this.onWheel)
      el.removeEventListener('touchstart', this.onTouchStart)
      el.removeEventListener('touchmove', this.onTouchMove)
    }
    // 组件销毁时兜底还原 .main（比如弹窗开着就直接切走了页面）
    this.dialogScrollLock = false
    this.applyDialogScrollLock()
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  },
  methods: {
    now() {
      return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
    },
    // =====「在做」的定时提醒（RunningTimerDialog）=====
    // 弹窗挂在 .frame 根下，用户在任何子页都能处理提醒；
    // 「结束」会打后端，并用 window 事件把结果同步回正在渲染的那张表（ListPage.onRunningEnded）。
    /** 「稍后说」：块继续在做，但不重新定时 */
    onTimerDismiss() {
      resolvePending('dismiss')
    },
    /** 「继续（再定时）」：按弹窗里选的新时刻重排定时 */
    onTimerContinue({ at, label }) {
      resolvePending('continue', at, label)
    },
    /**
     * 「结束」：写实际结束时间，块转成普通时间块。
     * 走 utils/runningEnd.js 的共享实现，和表格页「结束」按钮完全一致 ——
     * 用户第二天才从提醒弹窗里结束时，同样会**跨天拆块**（当天一段 + 之后每天一段）。
     */
    async onTimerEnd() {
      const entry = timerState.queue[0]
      if (!entry) return
      let result
      try {
        // 不传 block：这里只有定时条目（title / startedAt），块的 tagId / content 让 util 自己拉详情
        result = await endRunningBlock({ id: entry.id })
      } catch (e) {
        console.error('[TaskBlockPage] 定时弹窗里结束时间块失败', e)
        this.$message.error((e && e.message) || '结束失败，请稍后重试')
        return
      }
      // 先广播再出队：ListPage（若挂着）据此把本地那一条重新派生
      window.dispatchEvent(new CustomEvent('taskblock:running-ended', {
        detail: { id: String(entry.id), actualEndTime: result.actualEndTime }
      }))
      // 跨天拆出来的新块也广播出去（listener 见 ListPage.mounted）：
      // 只在目标日期就在本周列里时才补本地，不在本周就翻过去自然能看到
      const blocks = result.created.filter(b => b.id)
      if (blocks.length) {
        window.dispatchEvent(new CustomEvent('taskblock:running-split', {
          detail: {
            blocks: blocks.map(b => ({
              dateStr: b.dateStr,
              start: b.startMin,
              end: b.endMin,
              id: b.id,
              plannedStartTime: b.params.plannedStartTime,
              plannedEndTime: b.params.plannedEndTime,
              actualStartTime: b.params.actualStartTime,
              actualEndTime: b.params.actualEndTime,
              content: b.params.content,
              color: b.params.color
            }))
          }
        }))
      }
      resolvePending('end')
      const failed = result.created.filter(b => !b.id).length
      notifySuccess('已结束', result.created.length && !failed
        ? `结束时间已写入：${result.actualEndTime.slice(11, 16)}，并为之后 ${result.created.length} 天各记了一段`
        : `结束时间已写入：${result.actualEndTime.slice(11, 16)}`)
      if (failed) {
        this.$message.warning(`有 ${failed} 天的跨天记录没建成功，请手动补录`)
      }
    },
    /** 当前时刻的本地 ISO 串（yyyy-MM-ddTHH:mm:ss，与 deriveEventFields / 后端同格式） */
    buildIsoNow() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    /**
     * 把工作表名称按关键词切成「命中 / 未命中」的片段，模板逐段渲染即可高亮。
     *
     * 刻意**不用 v-html**：名称是用户在新建工作表时自己填的，拼 HTML 会有注入风险。
     * 这里切成纯文本片段交给 Vue 渲染，天然安全，也不用写转义逻辑。
     * 没在搜索时直接返回整段，省掉无意义的切分开销。
     */
    nameParts(name) {
      const text = name || ''
      const q = this.searchKeyword
      if (!q) return [{ text, hit: false }]

      const lower = text.toLowerCase()
      const parts = []
      let i = 0
      while (i < text.length) {
        const idx = lower.indexOf(q, i)
        if (idx === -1) {
          parts.push({ text: text.slice(i), hit: false })
          break
        }
        if (idx > i) parts.push({ text: text.slice(i, idx), hit: false })
        parts.push({ text: text.slice(idx, idx + q.length), hit: true })
        i = idx + q.length
      }
      return parts
    },
    clearSearch() {
      this.searchQuery = ''
    },
    onResize() {
      this.isNarrow = window.innerWidth <= 1100
      const max = this.fullHeaderHeight
      if (this.collapseOffset > max) this.collapseOffset = max
      if (this.renderOffset > max) this.renderOffset = max
      // 切换断点时不走动画，直接落位
      this.renderOffset = this.collapseOffset
      this.applyHeader()
    },
    normalizeWheelDelta(e) {
      let d = e.deltaY
      if (e.deltaMode === 1) {
        d *= 16
      } else if (e.deltaMode === 2) {
        d *= 400
      }
      return Math.max(-120, Math.min(120, d))
    },
    onWheel(e) {
      // 「新建／编辑工作表」弹窗打开时，本页手势整体停摆。
      // 这里**不能** preventDefault：要放行事件让弹窗内容区自己滚。
      if (this.dialogScrollLock) return
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      // 滚轮是一格一格的离散跳变，需要插值抹平
      this.smoothing = 0.28
      this.applyDelta(this.normalizeWheelDelta(e), e)
    },
    onTouchStart(e) {
      // 弹窗打开时不记录起点：否则关闭弹窗那一瞬，lastTouchY 还停在
      // 弹窗区域的旧坐标上，一滑动会先"跳"一大段。
      if (this.dialogScrollLock) return
      if (e.touches.length === 1) {
        this.lastTouchY = e.touches[0].clientY
      }
    },
    onTouchMove(e) {
      // 同上，停手势但不 preventDefault，触摸滚动留给弹窗内容区自己处理
      if (this.dialogScrollLock) return
      if (e.touches.length !== 1) return
      // 触摸本身连续，跟随即可（1 = 当帧直接落位）
      this.smoothing = 1
      const y = e.touches[0].clientY
      const delta = this.lastTouchY - y
      this.lastTouchY = y
      this.applyDelta(delta, e)
    },
    applyDelta(delta, e) {
      const max = this.fullHeaderHeight
      if (delta > 0) {
        // 下滑：优先折叠欢迎区，折完再把滚动交给内部容器
        if (this.collapseOffset < max) {
          if (e && e.cancelable) e.preventDefault()
          this.collapseOffset = Math.min(max, this.collapseOffset + delta)
          this.scheduleFrame()
        }
        return
      }
      if (delta < 0) {
        // 上滑：内部容器先回滚到顶，再展开欢迎区
        const scroller = e && e.target ? this.resolveScroller(e.target) : null
        if (scroller && scroller.scrollTop > 0) return
        if (this.collapseOffset > 0) {
          if (e && e.cancelable) e.preventDefault()
          this.collapseOffset = Math.max(0, this.collapseOffset + delta)
          this.scheduleFrame()
        }
      }
    },
    // 同一帧内多个 wheel/touch 事件只做一次计算与一次 DOM 写入
    scheduleFrame() {
      if (this.rafId) return
      this.lastTick = 0
      this.rafId = window.requestAnimationFrame(this.tick)
    },
    tick() {
      this.rafId = 0
      const target = this.collapseOffset
      const diff = target - this.renderOffset
      if (Math.abs(diff) < 0.5) {
        if (this.renderOffset !== target) {
          this.renderOffset = target
          this.applyHeader()
        }
        return
      }
      // 按真实帧间隔归一化，保证 60Hz / 120Hz 手感一致
      const t = this.now()
      const dt = this.lastTick ? Math.min(50, t - this.lastTick) : 16.7
      this.lastTick = t
      const k = 1 - Math.pow(1 - this.smoothing, dt / 16.7)
      this.renderOffset += diff * k
      this.applyHeader()
      this.scheduleFrame()
    },
    // 热路径直接写 DOM，不走 Vue 响应式，避免整个组件重渲染
    applyHeader() {
      const full = this.fullHeaderHeight
      const offset = Math.min(full, Math.max(0, this.renderOffset))
      const header = this.$refs.header
      if (header) {
        const next = Math.max(0, full - offset) + 'px'
        if (header.style.height !== next) header.style.height = next
      }
      const box = this.$refs.userBox
      if (box) {
        const p = full ? offset / full : 0
        const next = String(Math.max(0, Math.min(1, 1 - p * 1.6)))
        if (box.style.opacity !== next) box.style.opacity = next
      }
    },
    // getComputedStyle 会触发样式重算，按目标元素缓存，避免每次事件都算
    resolveScroller(target) {
      const now = this.now()
      if (this.scrollerTarget === target && now - this.scrollerTime < 300) {
        return this.scroller
      }
      this.scrollerTarget = target
      this.scrollerTime = now
      this.scroller = this.findScrollableAncestor(target)
      return this.scroller
    },
    findScrollableAncestor(el) {
      let node = el
      const stop = this.$refs.frame
      while (node && node !== stop && node.nodeType === 1) {
        const oy = window.getComputedStyle(node).overflowY
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
          return node
        }
        node = node.parentElement
      }
      return null
    },
    onChange(key) {
      // 'home' -> /taskblock
      // 其他 -> /taskblock/{key}
      if (key === 'home') {
        router.go('/taskblock')
      } else {
        router.go('/taskblock/' + key)
      }
    },
    goSubPage(key) {
      this.onChange(key)
    },
    async loadWorkspaceAndProjects() {
      try {
        const wsRes = await listWorkspaces()
        if (!wsRes.success || wsRes.code !== 200 || !wsRes.data || !wsRes.data.length) {
          console.warn('未获取到工作区列表', wsRes.message)
          return
        }
        const workspace = wsRes.data[0]
        const workspaceId = String(workspace.id)
        this.currentWorkspaceId = workspaceId
        localStorage.setItem('taskblock-current-workspace-id', workspaceId)
        console.log('[TaskBlockPage] current workspaceId:', workspaceId)

        const projRes = await listProjects(workspaceId)
        if (!projRes.success || projRes.code !== 200) {
          console.warn('获取工作表列表失败', projRes.message)
          return
        }
        this.workspaces = (projRes.data || []).map(project => this.mapProjectToWorkspace(project))
        if (!this.currentProjectId && this.workspaces.length) {
          this.currentProjectId = this.workspaces[0].id
        }
      } catch (e) {
        console.error('加载工作区或工作表失败', e)
      }
    },
    mapProjectToWorkspace(project) {
      const icon = project.icon || ''
      const preset = PRESET_ICONS.find(i => i.name === icon)
      const iconClass = preset ? preset.class : ''
      const letter = icon.length === 1 ? icon.toUpperCase() : (project.name || '').trim().charAt(0).toUpperCase()
      return {
        id: project.id,
        name: project.name,
        description: project.description || '',
        letter,
        iconClass,
        color: project.color || '#a8c985',
        pinned: this.pinnedIds.includes(String(project.id)),
        raw: project
      }
    },
    loadPinnedIds() {
      try {
        const raw = localStorage.getItem('taskblock-pinned-project-ids')
        this.pinnedIds = raw ? JSON.parse(raw) : []
      } catch (e) {
        this.pinnedIds = []
      }
    },
    savePinnedIds() {
      try {
        localStorage.setItem('taskblock-pinned-project-ids', JSON.stringify(this.pinnedIds))
      } catch (e) {
        // ignore
      }
    },
    /**
     * 接收 ListPage 上抛的「自己的实时状态」，渲染在左侧底部。
     * 与好友页一致：状态随区间响应一并下发，刷新时间表时同步更新。
     */
    onActivityChanged(activity) {
      this.selfActivity = activity || null
    },
    openCreateWorkspace() {
      this.dialogMode = 'create'
      this.editingProject = null
      this.showCreateWorkspace = true
    },
    /**
     * 「新建工作表」弹窗开/关时启停本页的折叠手势与内部滚动。
     *
     * 为什么必须在这儿拦：这个手势不是 Vue 模板上的 @wheel，而是在 mounted 里
     * 用 el.addEventListener 直接挂在 .frame 上的原生监听 —— 弹窗 DOM 就在
     * .frame 内部，滚轮 / 触摸事件冒泡上去照样被它接收，于是弹窗打开时
     * 背景（欢迎区 + .main 列表）还能被滚走。
     *
     * 弹窗内部自己是锁的（body overflow + overscroll-behavior + touchmove 兜底），
     * 但那套锁只能管住「页面级」滚动，管不到这层自研手势，所以这里额外补一刀：
     *  - 手势层：用 dialogScrollLock 短路 onWheel / onTouchMove；
     *  - 容器层：把 .main 的 overflow 落成 hidden，把浏览器原生滚动也一并关掉，
     *    否则触摸被 preventDefault 拦掉、滚轮又被短路，会出现"滚不动但内容在下面"的假死感。
     */
    onDialogVisibleChange(visible, backgroundSilent) {
      this.dialogScrollLock = visible && backgroundSilent !== false
      this.applyDialogScrollLock()
    },
    applyDialogScrollLock() {
      const main = document.querySelector('.tb-container .main')
      if (!main) return
      if (this.dialogScrollLock) {
        // 记下原值再落 hidden，关闭时原样还原，避免踩坏 .main 自己的样式
        if (this.savedMainOverflow === null) {
          this.savedMainOverflow = main.style.overflow
        }
        main.style.overflow = 'hidden'
      } else if (this.savedMainOverflow !== null) {
        main.style.overflow = this.savedMainOverflow
        this.savedMainOverflow = null
      }
    },
    /**
     * 打开 / 关闭「分享我的内容给好友」弹窗。
     *
     * 弹窗打开期间同样要把本页的折叠手势停掉 —— 原因与「新建工作表」弹窗完全一致：
     * 手势是挂在 .frame 上的原生 wheel / touchmove 监听，弹窗 DOM 就在 .frame 内，
     * 事件照样冒泡上去，不锁的话滚轮会把背后的欢迎区滚走。
     * 关闭时只有当「新建工作表」弹窗没开着才真正解锁，避免互相踩。
     */
    openShareDialog() {
      this.shareVisible = true
      this.dialogScrollLock = true
      this.applyDialogScrollLock()
    },
    closeShareDialog() {
      this.shareVisible = false
      if (!this.showCreateWorkspace) {
        this.dialogScrollLock = false
        this.applyDialogScrollLock()
      }
    },
    onWorkspaceCreated(project) {
      // 创建成功后刷新列表，保证排序与后端一致
      if (this.currentWorkspaceId) {
        this.loadWorkspaceAndProjects().then(() => {
          this.pinProject(project.id)
        })
      } else {
        this.workspaces.push(this.mapProjectToWorkspace(project))
        this.pinProject(project.id)
      }
      // 为新建工作表自动创建默认分类标签
      this.createDefaultTags(project.id)
    },
    /**
     * 新建工作表时创建四个默认分类。
     * 名称/图标/配色统一取自 `DEFAULT_CATEGORY_PRESETS`（constants.js）：
     * - 图标**拼进 name**（`fa:house生活`），不是单独的 icon 字段
     * - color 存的是「描边色」那一档，渲染时由 categoryPalette 派生出底色与文字色
     */
    async createDefaultTags(projectId) {
      for (const preset of DEFAULT_CATEGORY_PRESETS) {
        try {
          await createTag({
            projectId,
            name: buildTagName('fa', preset.icon, preset.name),
            color: preset.color,
            icon: ''
          })
        } catch (e) {
          console.error(`创建默认标签 "${preset.name}" 失败`, e)
        }
      }
    },
    pinProject(id) {
      const sid = String(id)
      if (!this.pinnedIds.includes(sid)) {
        this.pinnedIds = [...this.pinnedIds, sid]
        this.savePinnedIds()
        // 同步更新 workspaces 中的 pinned 状态
        const ws = this.workspaces.find(w => String(w.id) === sid)
        if (ws) {
          ws.pinned = true
        }
      }
    },
    unpinProject(id) {
      const sid = String(id)
      this.pinnedIds = this.pinnedIds.filter(pid => pid !== sid)
      this.savePinnedIds()
      const ws = this.workspaces.find(w => String(w.id) === sid)
      if (ws) {
        ws.pinned = false
      }
    },
    editWorkspace(ws) {
      this.dialogMode = 'edit'
      this.editingProject = ws.raw || ws
      this.showCreateWorkspace = true
    },
    onWorkspaceUpdated(project) {
      const idx = this.workspaces.findIndex(w => String(w.id) === String(project.id))
      if (idx !== -1) {
        this.workspaces.splice(idx, 1, this.mapProjectToWorkspace(project))
      }
    },
    togglePin(ws) {
      if (ws.pinned) {
        this.unpinProject(ws.id)
      } else {
        this.pinProject(ws.id)
      }
    },
    async deleteWorkspace(ws) {
      if (!window.confirm(`确定要删除工作表 "${ws.name}" 吗？`)) {
        return
      }
      try {
        const res = await deleteProject(ws.id)
        if (!res.success || res.code !== 200) {
          window.alert(res.message || '删除失败')
          return
        }
        this.workspaces = this.workspaces.filter(w => String(w.id) !== String(ws.id))
        this.unpinProject(ws.id)
        if (String(this.currentProjectId) === String(ws.id) && this.workspaces.length) {
          this.currentProjectId = this.workspaces[0].id
        }
      } catch (e) {
        console.error('删除工作表失败', e)
        window.alert('删除失败，请稍后重试')
      }
    },
    selectWorkspace(ws) {
      this.currentProjectId = ws.id
    },
    async openCreateTag() {
      if (!this.currentProjectId) {
        window.alert('请先选择一个工作表')
        return
      }
      const name = window.prompt('请输入自定义分类名称：')
      if (!name || !name.trim()) {
        return
      }
      try {
        const res = await createTag({
          projectId: this.currentProjectId,
          name: name.trim()
        })
        if (!res.success || res.code !== 200) {
          window.alert(res.message || '创建分类失败')
          return
        }
        window.alert('分类创建成功')
      } catch (e) {
        console.error('创建分类失败', e)
        window.alert('创建失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
/*
 * taskblock 作为独立子网站，全屏显示。
 * 现在 App.vue 已移除全局 html{zoom:1.5}，
 * 所以可以直接使用 width/height: 100vw/100vh，
 * 简洁且标准，无需任何 zoom 反向缩放技巧。
 */
.frame{
  position:fixed;
  top:0;
  left:0;
  width:100vw;
  height:100vh;
  border:none;
  border-radius:0;
  background:transparent;
  overflow:hidden;
  padding:0;
  display:flex;
  flex-direction:column;
}
.frame::after{
  content:"";
  position:absolute;
  top:0; left:0; right:0; bottom:0;
  background:rgba(247,237,225,0.5);
  z-index:-1;
}
.frame > *{ position:relative; }
/*
 * ⚠️ 兜底：`.frame > *{position:relative}` 会把 el-dialog 一起打回文档流。
 * .el-dialog__wrapper 自己是 position:fixed（Element theme-chalk，(0,1,0)），
 * 但上面那条 scoped 编译后是 `.frame > *[data-v]` = (0,2,0)，会把 fixed 压成 relative
 * → 弹窗不再是悬浮窗，而是变成页面底部凭空多出来的一个板块。
 * 「分享我的内容给好友」当初挂在 .frame 根下就是这个现象（已移进 .tb-container）。
 * 这里用 (0,3,0) 显式钉回 fixed，以后再往根下挂 el-dialog 也不会踩同一个坑。
 */
.frame >>> .el-dialog__wrapper{
  position:fixed;
}
/*
 * ⚠️ 同一个坑：成就解锁浮窗（AchievementUnlockDialog 的根节点 .unlock-overlay）也挂在 .frame 根下，
 * 子组件根节点会带上本组件的 scope 属性 → 被上面的 `.frame > *` 命中（同为 (0,2,0)，且本文件样式在
 * 组件样式之后输出，于是后写的 relative 胜出），浮窗被压成文档流里的普通块，
 * 表现为「解锁时页面底部凭空多出一块，把原本的内容往上挤」。
 * 这里同样用 (0,3,0) 钉回 fixed。
 */
.frame >>> .unlock-overlay{
  position:fixed;
}
.frame > .task-nav{
  position:relative;
  z-index:100;
  flex:0 0 auto;
}

/* 顶部 header（test.html 风格）
 * 高度由 JS 手势驱动（270/210 -> 0），不再写死，
 * 收掉的高度由下方 .tb-container / .tb-content 通过 flex:1 自动吃掉。
 *
 * 壁纸放在固定高度的 .tb-header-bg 上而不是 header 本身：
 * header 高度每帧变化时，背景图不会跟着重新计算 cover 缩放 / 重新栅格化，
 * 只做裁剪，滑动明显更顺。
 */
.tb-header{
  position:relative;
  flex:0 0 auto;
  overflow:hidden;
}
.tb-header-bg{
  position:absolute;
  top:0;
  left:0;
  right:0;
  height:270px;
  z-index:0;
  background-image:var(--tb-wallpaper, url('https://image.suisuisu.cn/upload/2607/d1b502465354b537.png'));
  background-size:cover;
  background-position:center;
}
.tb-header-bg::after{
  content:"";
  position:absolute;
  inset:0;
  background:var(--tb-header-overlay, rgba(216,232,200,0.45));
}

.user-box{
  position:absolute;
  z-index:1;
  right:1.875rem;
  top:6.25rem;
  background:#f8f4d8;
  border:3px solid #536b3a;
  padding:0.875rem 1.125rem;
  width:240px;
  box-shadow:3px 3px 0 #35512d;
  text-align:right;
}
.user-name{
  font-size:1rem;
  font-weight:bold;
  color:#35512d;
  margin-bottom:0.25rem;
}
.user-sub{
  font-size:0.75rem;
  color:#5a3a1a;
}

/* 两栏容器（test.html 风格） */
.tb-container{
  display:grid;
  grid-template-columns:20% 80%;
  gap:0.938rem;
  padding:0.938rem 1.5rem;
  flex:1 1 auto;
  min-height:0;
  overflow:hidden;
}

.tb-content{
  padding:0.938rem 1.5rem;
  flex:1 1 auto;
  min-height:0;
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

/* 子页面始终占满剩余视口高度（覆盖各子页 scoped 的 height:100%/overflow:hidden） */
.tb-content >>> .friends-page,
.tb-content >>> .thoughts-page,
.tb-content >>> .profile-page{
  height:100% !important;
  overflow:hidden !important;
  flex:1 1 auto;
  min-height:0;
}

.panel{
  background:#f5f1d5;
  border:4px solid #698b52;
  border-radius:0.5rem;
  padding:0.938rem;
  box-shadow:3px 3px 0 #587342;
  display:flex;
  flex-direction:column;
  min-height:0;
  min-width:0;
}

.sidebar{
  overflow:hidden;
  min-width:0;
  min-height:0;
  display:flex;
  flex-direction:column;
}
/* 兜底：窗口很矮时内容不会被裁掉且无法触达，
 * 这里允许内部滚动并隐藏滚动条，页面本身仍然永不滚动。
 */
.main{
  overflow-y:auto;
  overflow-x:hidden;
  min-height:0;
  position:relative;
  scrollbar-width:none;
  -ms-overflow-style:none;
}
.main::-webkit-scrollbar{
  display:none;
}

/* 仓库面板（来自 test.html） */
.repo-panel{
  width:100%;
  min-height:0;
  flex:1;
  background:#edf4d8;
  border:4px solid #57795c;
  border-radius:18px;
  padding:18px;
  box-shadow:
    0 6px 0 #426246,
    0 12px 20px rgba(0,0,0,.15);
  display:flex;
  flex-direction:column;
  overflow:hidden;
}

.repo-panel .header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:12px;
  flex-shrink:0;
}

.repo-panel .title{
  display:flex;
  align-items:center;
  gap:10px;
  font-size:22px;
  font-weight:bold;
  color:#263b29;
}

/* 「分享我的内容给好友」入口：跟在「我的工作区」标题右边。
   默认低调（透明底 + 次级灰），悬停才显主色，避免抢走标题的视觉重心。
   图标 20px（介于标题 22px 与列表行内按钮 16px 之间，跟标题同一行时不显突兀）。 */
.repo-panel .share-btn{
  flex:0 0 auto;
  width:30px;
  height:30px;
  padding:0;
  display:flex;
  align-items:center;
  justify-content:center;
  border:none;
  border-radius:10px;
  background:transparent;
  color:#71866a;
  cursor:pointer;
  transition:background .15s ease, color .15s ease;
}

.repo-panel .share-btn:hover{
  background:#e9f1d5;
  color:#426246;
}

.repo-panel .logo{
  width:32px;
  height:32px;
  background:#fff;
  border-radius:50%;
  border:3px solid #394d39;
}

.repo-panel .add-btn{
  width:36px;
  height:36px;
  border-radius:10px;
  border:3px solid #55775b;
  background:#e9f1d5;
  font-size:24px;
  color:#426246;
  cursor:pointer;
}

/*
 * 搜索框。
 *
 * ⚠️ 关键修复：input 必须写 min-width:0。
 * <input> 的 min-width 默认是 auto，会解析成它的「内在最小宽度」
 * （由 size 属性决定，默认约 20 个字符 ≈ 170px+），于是 flex 项拒绝收缩到这个宽度以下。
 * 面板变窄时 input 不再变小，就把右边的图标挤出容器 —— 这就是"页面一缩小图标就错位"。
 * 同理，两侧图标要 flex:0 0 auto，它们绝不参与收缩，永远贴在输入框两端。
 */
.repo-panel .search{
  height:42px;
  background:#f7f7df;
  border:3px solid #91a67b;
  border-radius:12px;
  display:flex;
  align-items:center;
  gap:8px;
  padding:0 12px;
  margin-bottom:14px;
  flex-shrink:0;
  min-width:0;
}

.repo-panel .search input{
  flex:1 1 0;
  min-width:0;      /* ← 修复图标错位的根本：允许输入框收缩到容器宽度以内 */
  width:100%;
  border:none;
  outline:none;
  background:transparent;
  font-size:15px;
}

/* 两端图标：固定尺寸、不收缩、不参与基线计算，保证任何宽度下都垂直居中对齐 */
.repo-panel .search .search-icon,
.repo-panel .search .search-clear{
  flex:0 0 auto;
  display:flex;
  align-items:center;
  justify-content:center;
}
.repo-panel .search .search-clear{
  width:20px;
  height:20px;
  padding:0;
  border:none;
  border-radius:50%;
  background:transparent;
  cursor:pointer;
}

.repo-panel .repo-list{
  flex:1;
  min-height:0;
  display:flex;
  flex-direction:column;
  gap:8px;
  overflow:auto;
}

.repo-panel .repo-item{
  height:52px;
  background:#edf2d5;
  border:3px solid #71866a;
  border-radius:10px;
  display:flex;
  align-items:center;
  padding:0 12px;
  transition:.15s;
  cursor:pointer;
}

.repo-panel .repo-item.active{
  background:#f3dc79;
  border-color:#856f30;
}

.repo-panel .repo-item:hover{
  background:#f7e99a;
  border-color:#8c7a38;
  box-shadow:
    inset 0 0 0 2px rgba(255,255,255,.4),
    0 3px 6px rgba(0,0,0,.12);
}

.repo-panel .repo-icon{
  width:32px;
  height:32px;
  margin-right:12px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:20px;
  font-weight:bold;
}

.repo-panel .repo-name{
  flex:1;
  color:#263525;
  font-weight:bold;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.repo-panel .repo-actions{
  display:flex;
  align-items:center;
  gap:8px;
  opacity:0;
  transition:opacity .15s;
}

.repo-item:hover .repo-actions{
  opacity:1;
}

.repo-panel .repo-action-btn{
  width:34px;
  height:34px;
  border:none;
  border-radius:8px;
  background:transparent;
  color:#71866a;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:16px;
  transition:all .15s;
}

.repo-panel .repo-action-btn:hover{
  color:#5a3a1a;
  transform:scale(1.1);
}

.repo-panel .repo-action-btn.pin i{
  transform:rotate(45deg);
}

.repo-item.pinned .repo-actions{
  opacity:1;
}

.repo-panel .repo-action-btn.pin.active{
  color:#e0ae21;
  background:#f3dc79;
}

.repo-panel .repo-action-btn.pin.active:hover{
  color:#9e7a10;
  background:#f3dc79;
}

.repo-panel .repo-action-btn.danger:hover{
  color:#dc2626;
}

.repo-panel .create{
  width:100%;
  height:52px;
  /* 上移到列表上方后，上下都留出间距（原来只在底部，靠 margin-top 顶开） */
  margin:16px 0 12px;
  background:#f1dc7b;
  border:3px solid #987a2c;
  border-radius:12px;
  display:flex;
  justify-content:center;
  align-items:center;
  gap:10px;
  font-size:18px;
  font-weight:bold;
  cursor:pointer;
  box-shadow:
    0 4px 0 #8a702c;
  flex-shrink:0;
}

.repo-panel .create:active{
  transform:translateY(3px);
  box-shadow:none;
}

/* 底部：自己的实时状态（在做 / 上次）。
   与好友列表昵称下的动态行复用同一个 FriendActivityLine 组件，排版/口径天然一致；
   无动态时整块 v-if 不渲染，列表自然撑满。 */
/*
  自己的实时状态卡片 —— 视觉与「朋友」页的 .friend-item 完全对齐：
  同一套圆角、描边、浅主色底、阴影、44px 头像与 15px/700 昵称，
  区别只是这里恒为自己，且不参与点击/选中态。
*/
.repo-panel .self-activity{
  flex-shrink:0;
  min-width:0;
  margin-top:12px;
}

.self-card{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
  padding:12px 14px;
  background:var(--lp-primary-soft,#e9f5ec);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}

/* 头像：与好友列表同一尺寸与配色（主色实心 + 深主色描边） */
.self-avatar{
  width:44px;
  height:44px;
  border-radius:50%;
  flex-shrink:0;
  font-size:16px;
  font-weight:bold;
  color:#fff;
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary-dark,#2f7a4a);
}

.self-info{
  flex:1;
  min-width:0;
}

.self-name{
  font-size:15px;
  font-weight:700;
  color:var(--lp-text,#26332b);
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.empty-tip{
  display:flex;
  align-items:center;
  justify-content:center;
  flex:1;
  color:#71866a;
  font-size:0.875rem;
  text-align:center;
  padding:2rem;
}

/* ListPage 板块切换动画 */
.list-page-switch-enter-active,
.list-page-switch-leave-active{
  transition:opacity .25s ease, transform .25s ease;
}
.list-page-switch-enter{
  opacity:0;
  transform:translateX(20px);
}
.list-page-switch-leave-to{
  opacity:0;
  transform:translateX(-20px);
}

/* 让 ListPage 适配 taskblock 面板：移除 ListPage 自带边框/内边距。
 *
 * 注意这里刻意不再把高度锁死成 100% / overflow:hidden：
 * 那样表格下半截和时间分配表会被直接裁掉，外层 .main 也检测不到溢出、滚不动。
 * 改成「内容多高就多高，至少占满一屏」，溢出的部分交回给 .main（overflow-y:auto）去滚。
 */
.panel >>> .frame{
  width:100%;
  min-width:0;
  display:block;
  height:auto;
  min-height:100%;
  flex-shrink:0;
  border:none;
  border-radius:0;
  background:transparent;
  padding:0;
  position:static;
  overflow:visible;
  box-sizing:border-box;
}
.panel >>> .frame::before,
.panel >>> .frame::after{
  display:none;
}
/* 解除 ListPage 内部 .frame > * { z-index:2 } 产生的堆叠上下文，
   让 QuickAddModal 等 fixed 弹窗能上浮到 .frame 根级 stacking context */
.panel >>> .frame > *{
  z-index:auto;
}
.panel >>> .frame > :not(.list-main){
  display:none;
}
.panel >>> .list-main{
  width:100%;
  min-width:0;
  margin-top:0;
  flex:0 0 auto;
}
.panel >>> .list-card{
  width:100%;
  min-width:0;
  height:auto;
  flex:0 0 auto;
  background:transparent;
  box-shadow:none;
  border-radius:0;
  padding:0;
  overflow:visible;
}
.panel >>> .tablebox{
  width:100%;
  min-width:0;
  overflow:visible;
  flex:0 0 auto;
}
.panel >>> table{
  width:100% !important;
}
.panel >>> .stats-table{
  width:100% !important;
}
.panel >>> .stats-table .cell{
  white-space:normal !important;
  line-height:1.2;
}

/* 提升“折叠设置”选择面板的层级，避免被下方表格覆盖 */
.panel >>> .settings-panel{
  z-index:100;
}

/* 将表格边框模型改为独立边框，避免 sticky 表头边框随 tbody 滚动丢失 */
.panel >>> table{
  border-collapse:separate !important;
  border-spacing:0 !important;
}
/* ⚠️ 这里**不要再写任何 `.panel >>> th / .panel >>> td{ border:… }`**。
   那些规则的优先级（(0,2,1) 起）高于 TimeTable 里 `thead th[data-v]` / `td:first-child[data-v]` 的
   (0,1,2)，会把表头边框、以及时间表外框的竖线一并清掉 —— 这正是之前"表头顶部边线消失"、
   "表头样式不得不在两个文件各写一份"（重复定义）的根源。
   **时间表的边框（含外框四条边 + 四角圆角）统一由 TimeTable 自己负责，这里一律不干预。** */
/* 统计表（TimeStats）：整表无网格，只留极淡的横向分隔线 */
.panel >>> .stats-table td,
.panel >>> .stats-table th{
  border:none;
  border-bottom:1px solid #eef5f0;
}

@media (max-width:1100px){
  .frame{ padding:0; }
  .tb-container{ grid-template-columns:1fr; overflow:auto; padding:0.938rem 1rem; }
  .tb-content{ overflow:auto; padding:0.938rem 1rem; }
  .tb-header-bg{ height:210px; }
  .user-box{ width:200px; padding:0.625rem 0.875rem; top:4.5625rem; }
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距沿用上面的原值。
   边框统一 2px（表格内部的分隔线仍是 1px），圆角按"微弧"收敛。
   上面媒体查询里的 .frame / .tb-container / .user-box 尺寸值没有被这里覆盖。 */
.frame::after{
  background:var(--lp-bg,#eef7f0);
}
.user-box{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  box-shadow:0 2px 10px rgba(52,102,68,.06);
}
.user-name{
  color:var(--lp-text,#26332b);
}
.user-sub{
  color:var(--lp-text-3,#94a59b);
}
/* 左右两个大面板：米黄厚涂 + 硬阴影 → 白底 + 柔阴影。
   边框用重一档的绿（--lp-border-strong），否则淡描边压在浅薄荷底上几乎看不出板块边界。
   3px 是为了让两个板块的轮廓更硬朗。 */
.panel{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:0 2px 12px rgba(52,102,68,.08);
}
/* 表头钉住时的「上方留白」。
   sticky 表头钉的是「滚动容器的 padding box 顶部」，于是：
   - 保留 .panel 的 15px 上内边距 → 表头钉住后，那 15px 里会漏出滚过去的内容；
   - 把内边距挪走 → 表头直接贴住面板边框，太挤（下滑时顶住板块顶部）。
   折中：用一根同样高度的 sticky 遮挡条顶住顶部（白色，z-index 高于表头），
   表头再钉在它下方（见文件末尾的 thead th 规则）。
   这样钉住时上方是干净的留白 —— 既不贴顶，也不漏内容；初始布局高度也不变。 */
.panel.main{
  padding-top:0;
}
.panel.main::before{
  content:"";
  display:block;
  flex:0 0 auto;
  height:0.938rem;
  position:sticky;
  top:0;
  z-index:40;
  background:var(--lp-surface,#fff);
}
.panel >>> .frame{
  padding-top:0;
}
.repo-panel{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:0.875rem;
  box-shadow:0 2px 12px rgba(52,102,68,.08);
}
.repo-panel .title{
  color:var(--lp-text,#26332b);
}
/* 分享入口：与列表行内按钮同一套配色（灰 → 悬停浅主色底 + 深主色图标） */
.repo-panel .share-btn{
  color:var(--lp-text-3,#94a59b);
}
.repo-panel .share-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.repo-panel .search{
  background:var(--lp-surface-2,#f7fbf8);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  transition:border-color .15s, box-shadow .15s;
}
/* 聚焦态挂在整框上（:focus-within），而不是只有 input —— input 本身是透明的，
   只描它的边看不出焦点在哪，整框高亮才符合"这是一个搜索框"的直觉 */
.repo-panel .search:focus-within{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:0 0 0 3px var(--lp-primary-soft,#e9f5ec);
}
.repo-panel .search input{
  color:var(--lp-text,#26332b);
}
.repo-panel .search input::placeholder{
  color:var(--lp-text-3,#94a59b);
}
.repo-panel .search .search-icon,
.repo-panel .search .search-clear{
  color:var(--lp-text-3,#94a59b);
}
.repo-panel .search .search-clear:hover{
  background:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 搜索结果计数：解释"为什么列表只剩这几条" */
.search-meta{
  flex-shrink:0;
  margin:-6px 0 10px;
  padding:0 2px;
  font-size:12px;
  color:var(--lp-text-3,#94a59b);
}
/* 列表空态：搜不到 / 压根还没有工作表 */
.repo-empty{
  flex-shrink:0;
  padding:22px 12px;
  border:2px dashed var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  font-size:13px;
  line-height:1.6;
  text-align:center;
  color:var(--lp-text-3,#94a59b);
  word-break:break-all;
}
/* 名称里被搜中的片段：淡主色底 + 加粗。
   刻意不改字体颜色去抢"收藏"的彩色字，只在底色上做区分 */
.repo-name .name-hit{
  background:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
  border-radius:3px;
  font-weight:700;
}
.repo-panel .repo-item{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
}
.repo-panel .repo-item:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary-soft2,#d5ebdb);
  box-shadow:none;
}
.repo-panel .repo-item.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
}
.repo-panel .repo-item.active:hover{
  background:var(--lp-primary-soft2,#d5ebdb);
  border-color:var(--lp-primary,#4aa46a);
}
.repo-panel .repo-name{
  color:var(--lp-text,#26332b);
  font-weight:500;
}
.repo-panel .repo-action-btn{
  color:var(--lp-text-3,#94a59b);
}
.repo-panel .repo-action-btn:hover{
  color:var(--lp-primary-dark,#2f7a4a);
  background:var(--lp-surface-2,#f7fbf8);
  transform:none;
}
.repo-panel .repo-action-btn.pin.active{
  color:var(--lp-primary-dark,#2f7a4a);
  background:var(--lp-primary-soft,#e9f5ec);
}
.repo-panel .repo-action-btn.pin.active:hover{
  color:var(--lp-primary-dark,#2f7a4a);
  background:var(--lp-primary-soft2,#d5ebdb);
}
.repo-panel .repo-action-btn.danger:hover{
  color:#d9534f;
  background:#fdecec;
}
.repo-panel .create{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary-dark,#2f7a4a);
  border-radius:0.875rem;
  color:#fff;
  box-shadow:none;
}
.repo-panel .create:active{
  transform:none;
  box-shadow:none;
  background:var(--lp-primary-dark,#2f7a4a);
}
.empty-tip{
  color:var(--lp-text-3,#94a59b);
}
/* 表头钉在留白条下方（0.938rem = 那根 sticky 遮挡条的高度），下滑时不直接顶住板块顶部。
   这里**只定义 sticky 偏移量**：留白条是本组件加的，TimeTable 不该知道它的存在。
   表头的 border-top / 圆角一律不在这里重复声明，统一由 TimeTable 的 `thead th` 负责
   （两条分隔线：表头那条 1px 也由 TimeTable 的 `th{border-bottom}` 提供）。 */
.panel >>> .tablebox thead th{
  top:0.938rem;
}
</style>
